import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from '@wordpress/element';

import {
    acceptGiveawayEligibility,
    claimPendingGiveaway,
    getGiveawayMe,
    startGiveawayLogin,
    verifyGiveawayLogin,
} from './giveawayApi';

const GiveawayContext = createContext(null);
const PENDING_CLAIM_KEY = 'bjnt_pending_giveaway_claim';

function playGiveawaySound(type = 'success') {
    try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;

        const context = new AudioContext();
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        const now = context.currentTime;
        const frequency = type === 'error' ? 180 : type === 'notice' ? 320 : 520;

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(frequency, now);
        oscillator.frequency.exponentialRampToValueAtTime(frequency * 1.35, now + 0.12);
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.08, now + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);

        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.start(now);
        oscillator.stop(now + 0.24);
    } catch (error) {
        // Sound is a nice-to-have; never block collection.
    }
}

export function useGiveaway() {
    const context = useContext(GiveawayContext);

    if (!context) {
        throw new Error('useGiveaway must be used inside GiveawayProvider');
    }

    return context;
}

function getFloatingTokenKey(token) {
    return [
        token?.sourceType || '',
        token?.sourceSlot || token?.slot || '',
        token?.id || token?.tokenId || '',
        token?.partnerId || '',
    ].join(':');
}

function normalizeClaimPack(tokenOrPack) {
    if (!tokenOrPack) return null;

    const tokens = Array.isArray(tokenOrPack.tokens)
        ? tokenOrPack.tokens.filter(Boolean)
        : [tokenOrPack].filter(Boolean);

    if (!tokens.length) return null;

    const first = tokens[0];
    const packId = tokenOrPack.packId
        || `${first.sourceType || ''}:${first.sourceSlot || first.slot || ''}:${tokens.map((token) => token.claimToken || token.pendingClaimId || token.tokenId || token.id).join('|')}`;

    return {
        ...first,
        ...tokenOrPack,
        tokens,
        packId,
        claimToken: first.claimToken || first.pendingClaimId,
        pendingClaimId: first.pendingClaimId || first.claimToken,
        sourceType: tokenOrPack.sourceType || first.sourceType,
        sourceSlot: tokenOrPack.sourceSlot || first.sourceSlot || first.slot,
        slot: tokenOrPack.slot || first.slot || first.sourceSlot,
    };
}

function getPackPendingIds(pack) {
    return (pack?.tokens || [])
        .map((token) => token.pendingClaimId || token.claimToken)
        .filter(Boolean);
}

export default function GiveawayProvider({ children }) {
    const [me, setMe] = useState({ loggedIn: false });
    const [activeToken, setActiveToken] = useState(null);
    const [floatingTokens, setFloatingTokens] = useState([]);
    const [showLogin, setShowLogin] = useState(false);
    const [showEligibility, setShowEligibility] = useState(false);
    const [showCollect, setShowCollect] = useState(false);
    const [collectedTokenAnimation, setCollectedTokenAnimation] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');

    const loggedIn = !!me?.loggedIn;

    const loadMe = useCallback(async () => {
        try {
            const data = await getGiveawayMe();
            setMe(data);
        } catch (error) {
            setMe({ loggedIn: false });
        }
    }, []);

    useEffect(() => {
        loadMe();
    }, [loadMe]);

    const removeFloatingToken = useCallback((claimToken) => {
        setFloatingTokens((current) =>
            current.filter((token) => token.claimToken !== claimToken)
        );
    }, []);

    const showRewardToken = useCallback((token) => {
        if (!token?.claimToken) return;

        const tokenWithMeta = {
            ...token,
            earnedAt: token.earnedAt || Date.now(),
            floatingKey: getFloatingTokenKey(token),
        };

        setFloatingTokens((current) => {
            const exists = current.some(
                (item) => item.floatingKey === tokenWithMeta.floatingKey
            );

            if (exists) return current;

            return [...current, tokenWithMeta];
        });
    }, []);

    const openToken = useCallback((tokenOrPack) => {
        const pack = normalizeClaimPack(tokenOrPack);

        if (!pack?.claimToken || loading) return;

        setActiveToken(pack);
        setMessage('');

        if (!loggedIn) {
            localStorage.setItem(PENDING_CLAIM_KEY, JSON.stringify({
                token: pack,
                pendingClaimIds: getPackPendingIds(pack),
                pendingClaimId: pack.pendingClaimId || pack.claimToken,
                savedAt: Date.now(),
            }));

            setShowLogin(true);
            return;
        }

        if (!me?.profileComplete) {
            setShowEligibility(true);
            return;
        }

        setShowCollect(true);
    }, [loggedIn, loading, me?.profileComplete]);

    const openLogin = useCallback(() => {
        setActiveToken(null);
        setMessage('');
        setShowCollect(false);
        setShowEligibility(false);
        setShowLogin(true);
    }, []);

    const closeModals = useCallback(() => {
        setShowLogin(false);
        setShowEligibility(false);
        setShowCollect(false);
        setActiveToken(null);
        setMessage('');
    }, []);

    const requestLoginCode = useCallback(async (email) => {
        setLoading(true);
        setMessage('');

        try {
            await startGiveawayLogin(email);
            setMessage('Login code sent. Check your email.');
            playGiveawaySound('notice');
            return true;
        } catch (error) {
            setMessage(error.message || 'Could not send login code.');
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    const verifyLoginCode = useCallback(async (email, code) => {
        setLoading(true);
        setMessage('');

        try {
            const data = await verifyGiveawayLogin(email, code);

            setMe({
                loggedIn: true,
                profileComplete: !!data.profileComplete,
                user: data.user,
            });

            setShowLogin(false);

            let pack = activeToken;
            const pendingRaw = localStorage.getItem(PENDING_CLAIM_KEY);

            if (pendingRaw && !pack) {
                try {
                    const pending = JSON.parse(pendingRaw);

                    if (
                        pending?.token &&
                        Date.now() - Number(pending.savedAt || 0) < 15 * 60 * 1000
                    ) {
                        pack = normalizeClaimPack(pending.token);
                        setActiveToken(pack);
                    }
                } catch (error) {
                    localStorage.removeItem(PENDING_CLAIM_KEY);
                }
            }

            if (pack) {
                if (data.profileComplete) {
                    setShowCollect(true);
                } else {
                    setShowEligibility(true);
                }
            } else {
                setShowCollect(false);
                setShowEligibility(false);
            }

            window.dispatchEvent(new CustomEvent('bjnt:giveaway-login'));

            return true;
        } catch (error) {
            setMessage(error.message || 'Invalid login code.');
            return false;
        } finally {
            setLoading(false);
        }
    }, [activeToken]);

    const acceptEligibility = useCallback(async (
        firstName,
        lastName,
        ageConfirmed,
        termsAccepted
    ) => {
        setLoading(true);
        setMessage('');

        try {
            await acceptGiveawayEligibility(
                firstName,
                lastName,
                ageConfirmed,
                termsAccepted
            );

            setMe((current) => ({
                ...current,
                profileComplete: true,
            }));

            setShowEligibility(false);
            setShowCollect(true);

            return true;
        } catch (error) {
            setMessage(error.message || 'Please accept the giveaway terms.');
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    const collectActiveToken = useCallback(async () => {
        const pack = normalizeClaimPack(activeToken);

        if (!pack?.claimToken || loading) return false;

        setLoading(true);
        setMessage('');

        const tokens = pack.tokens || [pack];
        const collected = [];
        const already = [];
        const failed = [];

        try {
            for (const token of tokens) {
                const pendingClaimId = token.pendingClaimId || token.claimToken;

                if (!pendingClaimId) {
                    failed.push(token);
                    continue;
                }

                try {
                    const data = await claimPendingGiveaway(pendingClaimId);

                    if (data?.success) {
                        collected.push({ token, data });
                        removeFloatingToken(token.claimToken);
                    } else if (data?.alreadyCollected) {
                        already.push({ token, data });
                        removeFloatingToken(token.claimToken);
                    } else {
                        failed.push({ token, data });
                    }
                } catch (error) {
                    failed.push({ token, error });
                }
            }

            const handledCount = collected.length + already.length;
            const entriesCreated = collected.reduce(
                (total, item) => total + Number(item.data?.claim?.entriesCreated || 0),
                0
            );

            if (handledCount) {
                if (collected.length) {
                    setCollectedTokenAnimation({
                        imageUrl: collected[0]?.token?.imageUrl,
                        name: collected[0]?.token?.name,
                    });

                    window.setTimeout(() => {
                        setCollectedTokenAnimation(null);
                    }, 1600);
                }

                playGiveawaySound(collected.length ? 'success' : 'notice');

                window.dispatchEvent(new CustomEvent('bjnt:giveaway-collected', {
                    detail: {
                        sourceType: pack.sourceType,
                        sourceSlot: pack.sourceSlot || pack.slot,
                        tokenIds: tokens.map((token) => token.tokenId || token.id).filter(Boolean),
                        partnerIds: tokens.map((token) => token.partnerId).filter(Boolean),
                    },
                }));
            }

            await loadMe();
            localStorage.removeItem(PENDING_CLAIM_KEY);

            if (failed.length && handledCount) {
                const collectedText = collected.length
                    ? `Collected ${collected.length} sticker${collected.length === 1 ? '' : 's'}.`
                    : 'Some stickers were already collected.';
                setMessage(`${collectedText} ${failed.length} could not be collected — try again.`);
                return false;
            }

            if (failed.length) {
                setMessage('Could not collect this sticker pack. Please try again.');
                playGiveawaySound('error');
                return false;
            }

            if (!collected.length && already.length) {
                setMessage(already.length === 1
                    ? 'You already collected this sticker today.'
                    : 'You already collected these stickers today.'
                );

                window.setTimeout(() => {
                    setShowCollect(false);
                    setActiveToken(null);
                    setMessage('');
                }, 1800);

                return true;
            }

            const stickerWord = collected.length === 1 ? 'sticker' : 'stickers';
            const entryWord = entriesCreated === 1 ? 'entry' : 'entries';
            const alreadyText = already.length
                ? ` ${already.length} sticker${already.length === 1 ? ' was' : 's were'} already collected.`
                : '';

            setMessage(`Collected ${collected.length} ${stickerWord}! You earned ${entriesCreated} ${entryWord}.${alreadyText}`);

            window.setTimeout(() => {
                setShowCollect(false);
                setActiveToken(null);
                setMessage('');
            }, 1400);

            return true;
        } finally {
            setLoading(false);
        }
    }, [activeToken, loading, loadMe, removeFloatingToken]);

    const value = useMemo(() => ({
        me,
        loggedIn,
        activeToken,
        showLogin,
        showEligibility,
        showCollect,
        loading,
        message,
        floatingTokens,
        collectedTokenAnimation,
        openToken,
        closeModals,
        openLogin,
        requestLoginCode,
        verifyLoginCode,
        acceptEligibility,
        collectActiveToken,
        showRewardToken,
        removeFloatingToken,
    }), [
        me,
        loggedIn,
        activeToken,
        floatingTokens,
        collectedTokenAnimation,
        showLogin,
        showEligibility,
        showCollect,
        loading,
        message,
        openToken,
        closeModals,
        openLogin,
        requestLoginCode,
        verifyLoginCode,
        acceptEligibility,
        collectActiveToken,
        showRewardToken,
        removeFloatingToken,
    ]);

    return (
        <GiveawayContext.Provider value={value}>
            {children}
        </GiveawayContext.Provider>
    );
}
